import * as React from 'react';
// Import form hooks.
import {
    useComparativeEntitySummary,
	UseComparativeEntitySummaryResults,
	useFormPopupMortgagee,
	UseFormPopupMortgageeResults,
} from '@app/hooks/view';
// Import props.
import {
	ComparativeMortgageeSummaryProps,
	ComparativeDataRowProps,
} from '@app/props';
// Import enumerations.
import {
	EntityInstanceType,
} from '@app/enumerations';
// Import api models.
import {
	Address,
    AddressVerification,
    GpsCoordinatesWithAccuracy,
    Mortgagee,
    MortgageeRequest,
    ThirdPartyPropertyDataCommercial,
    ThirdPartyPropertyDataPersonal,
} from '@app/models/api';
// Import interfaces.
import {
	IAddress,
} from '@app/interfaces';

interface UseComparativeMortgageeSummaryParameters {
	props: ComparativeMortgageeSummaryProps;
}
interface UseComparativeMortgageeSummaryResults {
	showTheAddressComparison: boolean;
	comparativeEntitySummary: UseComparativeEntitySummaryResults;
	applyAddressComparativeDataRowProps: () => ComparativeDataRowProps<IAddress>;
	applyMortgageeDetailsComparativeDataRowProps: () => ComparativeDataRowProps[];
}

const useComparativeMortgageeSummary = (params: UseComparativeMortgageeSummaryParameters): UseComparativeMortgageeSummaryResults => {

	// #region exported methods

	/**
	 * Returns address comparative data row props. 
	 **/
	const applyAddressComparativeDataRowProps = (): ComparativeDataRowProps<IAddress> => {
		const oldAddress: IAddress | undefined = oldFormPopupMortgagee.addressVerification.addressLocationInput.value.address
			?? newFormPopupMortgagee.addressVerification.addressLocationInput.value.address
			?? undefined;
		const newAddress: IAddress | undefined = newFormPopupMortgagee.addressVerification.addressLocationInput.value.address
			?? undefined;
		return {
			oldValue: oldAddress,
			newValue: newAddress,
			hideNewValueIfSame: true,
			hideDivider: true,
			paddingProps: {
				bottom: .5,
				top: .5,
			},
		};
	};

	/**
	 * Returns mortgagee details comparative data row props. 
	 **/
	const applyMortgageeDetailsComparativeDataRowProps = (): ComparativeDataRowProps[] => {
		return [
			{
				fieldLabel: oldFormPopupMortgagee.includeInterestsInput.label,
				oldValue: comparativeEntitySummary.isEntityAdded ? 'N/A' : oldFormPopupMortgagee.includeInterestsInput.getSelectedValueDisplayName()?.toTitleCase(),
				newValue: newFormPopupMortgagee.includeInterestsInput.getSelectedValueDisplayName()?.toTitleCase(),
				hideNewValueIfSame: !comparativeEntitySummary.isEntityAdded,
				hideNewValueIfUndefinedOrEmpty: comparativeEntitySummary.isEntityAdded || comparativeEntitySummary.isEntityRemoved,
			},
			{
				fieldLabel: oldFormPopupMortgagee.includeSuccessorOrAssignsInput.label,
				oldValue: comparativeEntitySummary.isEntityAdded ? 'N/A' : oldFormPopupMortgagee.includeSuccessorOrAssignsInput.getSelectedValueDisplayName()?.toTitleCase(),
				newValue: newFormPopupMortgagee.includeSuccessorOrAssignsInput.getSelectedValueDisplayName()?.toTitleCase(),
				hideNewValueIfSame: !comparativeEntitySummary.isEntityAdded,
				hideNewValueIfUndefinedOrEmpty: comparativeEntitySummary.isEntityAdded || comparativeEntitySummary.isEntityRemoved,
			},
			{
				fieldLabel: oldFormPopupMortgagee.lienholderPositionInput.label,
				oldValue: comparativeEntitySummary.isEntityAdded ? 'N/A' : oldFormPopupMortgagee.lienholderPositionInput.value?.toString(),
				newValue: newFormPopupMortgagee.lienholderPositionInput.value?.toString(),
				hideNewValueIfSame: !comparativeEntitySummary.isEntityAdded,
				hideNewValueIfUndefinedOrEmpty: comparativeEntitySummary.isEntityAdded || comparativeEntitySummary.isEntityRemoved,
			},
			{
				fieldLabel: oldFormPopupMortgagee.loanNumberInput.label,
				oldValue: comparativeEntitySummary.isEntityAdded ? 'N/A' : oldFormPopupMortgagee.loanNumberInput.value,
				newValue: newFormPopupMortgagee.loanNumberInput.value,
				hideNewValueIfSame: !comparativeEntitySummary.isEntityAdded,
				hideNewValueIfUndefinedOrEmpty: comparativeEntitySummary.isEntityAdded || comparativeEntitySummary.isEntityRemoved,
			},
			{
				fieldLabel: oldFormPopupMortgagee.mortgageeEmailInput.label,
				oldValue: comparativeEntitySummary.isEntityAdded ? 'N/A' : oldFormPopupMortgagee.mortgageeEmailInput.value,
				newValue: newFormPopupMortgagee.mortgageeEmailInput.value,
				hideNewValueIfSame: !comparativeEntitySummary.isEntityAdded,
				hideNewValueIfUndefinedOrEmpty: comparativeEntitySummary.isEntityAdded || comparativeEntitySummary.isEntityRemoved,
			},
			{
				fieldLabel: oldFormPopupMortgagee.sendPaymentReceiptToMortgageeInput.label,
				oldValue: comparativeEntitySummary.isEntityAdded || !oldFormPopupMortgagee.isSendPaymentReceiptToMortgageeRequired ? 'N/A' : oldFormPopupMortgagee.sendPaymentReceiptToMortgageeInput.getSelectedValueDisplayName()?.toTitleCase(),
				newValue: !newFormPopupMortgagee.isSendPaymentReceiptToMortgageeRequired ? 'N/A' : newFormPopupMortgagee.sendPaymentReceiptToMortgageeInput.getSelectedValueDisplayName()?.toTitleCase(),
				hideNewValueIfSame: !comparativeEntitySummary.isEntityAdded,
				hideNewValueIfUndefinedOrEmpty: comparativeEntitySummary.isEntityAdded || comparativeEntitySummary.isEntityRemoved,
			},
			{
				fieldLabel: oldFormPopupMortgagee.isMortgageeBilledInput.label,
				oldValue: comparativeEntitySummary.isEntityAdded ? 'N/A' : oldFormPopupMortgagee.isMortgageeBilledInput.getSelectedValueDisplayName()?.toTitleCase(),
				newValue: newFormPopupMortgagee.isMortgageeBilledInput.getSelectedValueDisplayName()?.toTitleCase(),
				hideNewValueIfSame: !comparativeEntitySummary.isEntityAdded,
				hideNewValueIfUndefinedOrEmpty: comparativeEntitySummary.isEntityAdded || comparativeEntitySummary.isEntityRemoved,
			},
			{
				fieldLabel: oldFormPopupMortgagee.isMortgageeRequiredInput.label,
				oldValue: comparativeEntitySummary.isEntityAdded ? 'N/A' : oldFormPopupMortgagee.isMortgageeRequiredInput.getSelectedValueDisplayName()?.toTitleCase(),
				newValue: newFormPopupMortgagee.isMortgageeRequiredInput.getSelectedValueDisplayName()?.toTitleCase(),
				hideNewValueIfSame: !comparativeEntitySummary.isEntityAdded,
				hideNewValueIfUndefinedOrEmpty: comparativeEntitySummary.isEntityAdded || comparativeEntitySummary.isEntityRemoved,
			},
		];
	};

	// #endregion exported methods


	// #region private methods

	/**
	 * Generates id for the mortgagee info form.
	 */
	const generateIdForMortgageeInfoForm = (instanceType: EntityInstanceType): string => {
		return `${params.props.id}-${instanceType}-mortgageeInfoForm`;
	}

	/**
	 * Creates mortgagee request instance.
	 */
	const createMortgageeRequest = (mortgagee: Mortgagee | undefined): MortgageeRequest => {
		let mortgageeRequest: MortgageeRequest = new MortgageeRequest();
		mortgageeRequest.address = mortgagee?.mailingAddress ?? null;
		mortgageeRequest.email = mortgagee?.email ?? null;
		mortgageeRequest.includeInterests = mortgagee?.includeAsTheirInterestsMayAppear ?? null;
		mortgageeRequest.includeSuccessorOrAssigns = mortgagee?.includeItsSuccessorsAndOrAssigns ?? null;
		mortgageeRequest.isMortgageeBilled = mortgagee?.billMortgagee ?? null;
		mortgageeRequest.isMortgageeRequired = mortgagee?.coverageRequiredByMortgagee ?? null;
		mortgageeRequest.loanNumber = mortgagee?.loanNumber ?? null;
		mortgageeRequest.name = mortgagee?.name ?? null;
		mortgageeRequest.positionNumber = mortgagee?.lienPositionNumber ?? null;
		mortgageeRequest.sendPaymentReceiptToMortgagee = mortgagee?.sendPaymentReceiptToEmail ?? null;

		return mortgageeRequest;	
	}

	// #endregion private methods


	// #region variables

	const oldMortgagee: MortgageeRequest = React.useMemo(
		() => createMortgageeRequest(params.props.comparisonEntitySummary.oldData ?? undefined),
		[params.props.comparisonEntitySummary.oldData],
	);
	const newMortgagee: MortgageeRequest = React.useMemo(
		() => createMortgageeRequest(params.props.comparisonEntitySummary.newData ?? undefined),
		[params.props.comparisonEntitySummary.newData],
	);

	// Old form popup mortgagee.
	const oldFormPopupMortgagee: UseFormPopupMortgageeResults = useFormPopupMortgagee({
		id: generateIdForMortgageeInfoForm(EntityInstanceType.OLD),
		props: {
			isOpen: false,
			showValidationErrorsOnMount: true,
			possibleStates: params.props.possibleStates,
			popupTitle: String.empty(),
			mortgagee: oldMortgagee,
			onSaveMortgagee: (mortgagee: MortgageeRequest) => { },
			addressProps: {
				geocodeAddress: async (address: Address) => new GpsCoordinatesWithAccuracy(),
				getAddressSuggestions: async (addressQuery: string) => [],
				verifyAddress: async (address: Address) => new AddressVerification(),
				verifySingleLineAddress: async (singleLineAddress: string) => new AddressVerification(),
				getThirdPartyPropertyDataCommercial: async (address: Address, lat: number, lng: number) => new ThirdPartyPropertyDataCommercial(),
				getThirdPartyPropertyDataPersonal: async (address: Address, lat: number, lng: number) => new ThirdPartyPropertyDataPersonal(),
			},
			onClose: () => { },
			mortgageeLienholderPositionsInUse: [],
			mortgageeEmailsInUse: [],
		},
	});

	// New form popup mortgagee.
	const newFormPopupMortgagee: UseFormPopupMortgageeResults = useFormPopupMortgagee({
		id: generateIdForMortgageeInfoForm(EntityInstanceType.NEW),
		props: {
			isOpen: false,
			showValidationErrorsOnMount: true,
			possibleStates: params.props.possibleStates,
			popupTitle: String.empty(),
			mortgagee: newMortgagee,
			onSaveMortgagee: (mortgagee: MortgageeRequest) => { },
			addressProps: {
				geocodeAddress: async (address: Address) => new GpsCoordinatesWithAccuracy(),
				getAddressSuggestions: async (addressQuery: string) => [],
				verifyAddress: async (address: Address) => new AddressVerification(),
				verifySingleLineAddress: async (singleLineAddress: string) => new AddressVerification(),
				getThirdPartyPropertyDataCommercial: async (address: Address, lat: number, lng: number) => new ThirdPartyPropertyDataCommercial(),
				getThirdPartyPropertyDataPersonal: async (address: Address, lat: number, lng: number) => new ThirdPartyPropertyDataPersonal(),
			},
			onClose: () => { },
			mortgageeLienholderPositionsInUse: [],
			mortgageeEmailsInUse: [],
		},
	});

	// Comparative entity summary.
	const comparativeEntitySummary: UseComparativeEntitySummaryResults = useComparativeEntitySummary({
		id: params.props.id,
		props: params.props,
	});

	// Checks to see if the address should be rendered if either the old or new mortgagee data is an address record.
	const showTheAddressComparison: boolean = React.useMemo(() => {
		return oldMortgagee.address !== null || newMortgagee.address !== null;
	}, [oldMortgagee, newMortgagee]);

	// #endregion variables

	return {
		showTheAddressComparison,
		comparativeEntitySummary,
		applyAddressComparativeDataRowProps,
		applyMortgageeDetailsComparativeDataRowProps,
	};
}

// Export parameters.
export {
	UseComparativeMortgageeSummaryParameters,
}

// Export results.
export {
	UseComparativeMortgageeSummaryResults,
}

export default useComparativeMortgageeSummary;